import React from 'react';
import {
  Box,
  Text,
  VStack,
  Heading,
  Grid,
  HStack,
  Button
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useSettings, useProjects, useFeaturedProject, useProjectStats } from '../hooks/useApi';
import { ProjectsPageSkeleton } from '../components/SkeletonComponents';

const Projects = () => {
  const { data: settings, loading: settingsLoading, error: settingsError } = useSettings();
  const { data: projectsData, loading: projectsLoading, error: projectsError } = useProjects();
  const { data: featuredProject, loading: featuredLoading, error: featuredError } = useFeaturedProject();
  const { data: projectStats, loading: statsLoading, error: statsError } = useProjectStats();

  // Loading state
  if (settingsLoading || projectsLoading || featuredLoading || statsLoading) {
    return <ProjectsPageSkeleton />;
  }
  
  // Error state
  const hasError = settingsError || projectsError || featuredError || statsError;
  if (hasError) {
    return (
      <Box py={{ base: 16, md: 20 }} px={4}>
        <VStack spacing={8} maxW="5xl" mx="auto">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center">
            OUR PROJECT PORTFOLIO
          </Heading>
          <Box bg="red.900" border="1px solid" borderColor="red.600" p={6} borderRadius="md" w="100%">
            <VStack spacing={3}>
              <Text color="red.300" fontSize={{ base: "md", md: "lg" }} fontWeight="bold">
                ⚠️ Error Loading Projects
              </Text>
              <Text color="white" textAlign="center">
                {settingsError || projectsError || featuredError || statsError}
              </Text>
              <Text color="gray.300" textAlign="center" fontSize={{ base: "xs", md: "sm" }}>
                Make sure your Django backend is running on localhost:8000
              </Text>
            </VStack>
          </Box>
        </VStack>
      </Box>
    );
  }

  // Helper function to parse technologies
  const parseTechnologies = (techString) => {
    if (!techString) return [];
    return techString.split(',').map(tech => tech.trim());
  };

  return (
    <Box py={{ base: 16, md: 20 }} px={4}>
      <VStack spacing={{ base: 16, md: 20 }} maxW="5xl" mx="auto">
        {/* Hero Section */}
        <VStack spacing={8} textAlign="center">
          <Heading size={{ base: "xl", md: "2xl", lg: "4xl" }} textTransform="uppercase" letterSpacing="wide">
            {settings?.projects_page_title || "OUR PROJECT PORTFOLIO"}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg", lg: "xl" }} color="gray.300" maxW="4xl" lineHeight="1.6">
            {settings?.projects_page_description || 
             "Explore our completed projects and case studies. From startup MVPs to enterprise-scale applications, see how we've helped teams build scalable, reliable systems that drive business results."
            }
          </Text>
        </VStack>

        {/* Featured Project */}
        {featuredProject && (
          <Box bg="gray.900" p={{ base: 6, md: 8, lg: 12 }} border="1px solid" borderColor="gray.700" w="100%">
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={{ base: 8, lg: 12 }} alignItems="center">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={3}>
                  <HStack spacing={3}>
                    <Box w={2} h={2} bg="green.500" borderRadius="full" />
                    <Text color="green.500" fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide">
                      FEATURED PROJECT
                    </Text>
                  </HStack>
                  <Heading size={{ base: "lg", md: "xl", lg: "2xl" }} textTransform="uppercase" letterSpacing="wide">
                    {featuredProject.title}
                  </Heading>
                  <Text color="gray.300" lineHeight="1.6" fontSize={{ base: "sm", md: "md" }}>
                    {featuredProject.short_description}
                  </Text>
                </VStack>
                
                <VStack align="start" spacing={4} w="100%">
                  <VStack align="start" spacing={2}>
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="bold" textTransform="uppercase" letterSpacing="wide">
                      TECHNOLOGIES
                    </Text>
                    <HStack wrap="wrap" spacing={2}>
                      {featuredProject.technologies_list?.map((tech, index) => (
                        <Box key={index} bg="green.500" px={2} py={1}>
                          <Text fontSize={{ base: "xs", md: "xs" }} color="white">{tech}</Text>
                        </Box>
                      ))}
                    </HStack>
                  </VStack>
                  <Link to={`/projects/${featuredProject.slug}`}>
                    <Button
                      bg="green.500"
                      color="white"
                      size="sm"
                      _hover={{ bg: "green.600" }}
                    >
                      View Case Study
                    </Button>
                  </Link>
                </VStack>
              </VStack>

              <Box
                h={{ base: "200px", md: "250px", lg: "300px" }}
                bg="gray.800"
                border="1px solid"
                borderColor="gray.600"
                display="flex"
                alignItems="center"
                justifyContent="center"
                position="relative"
                overflow="hidden"
                backgroundImage={featuredProject.featured_image ? `url(${featuredProject.featured_image})` : undefined}
                backgroundSize="cover"
                backgroundPosition="center"
              >
                {!featuredProject.featured_image && (
                  <>
                    <Text color="gray.400" fontSize={{ base: "xs", md: "sm" }} textAlign="center">
                      {featuredProject.title}
                      <br />
                      Screenshot
                    </Text>
                    <Box
                      position="absolute"
                      top="20%"
                      right="20%"
                      w="40px"
                      h="40px"
                      border="1px solid"
                      borderColor="green.500"
                    />
                    <Box
                      position="absolute"
                      bottom="30%"
                      left="25%"
                      w="30px"
                      h="30px"
                      bg="green.500"
                      opacity={0.7}
                      borderRadius="full"
                    />
                  </>
                )}
              </Box>
            </Grid>
          </Box>
        )}

        {/* Project Categories */}
        <VStack spacing={{ base: 8, md: 12 }} w="100%">
          <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center">
            PROJECT CATEGORIES
          </Heading>
          
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={{ base: 6, md: 8 }}>
            {/* Category 1 */}
            <Box bg="gray.900" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.700">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_1_title || "WEB APPLICATIONS"}
                  </Heading>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.projects_category_1_description || 
                     "Modern, responsive web applications built with React, Django, and other cutting-edge technologies."
                    }
                  </Text>
                  <Text color="green.500" fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_1_count || "15+ PROJECTS COMPLETED"}
                  </Text>
                </VStack>
              </VStack>
            </Box>

            {/* Category 2 */}
            <Box bg="gray.900" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.700">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_2_title || "API DEVELOPMENT"}
                  </Heading>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.projects_category_2_description || 
                     "RESTful APIs and GraphQL endpoints designed for scalability, security, and performance."
                    }
                  </Text>
                  <Text color="green.500" fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_2_count || "20+ APIS BUILT"}
                  </Text>
                </VStack>
              </VStack>
            </Box>

            {/* Category 3 */}
            <Box bg="gray.900" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.700">
              <VStack align="start" spacing={6}>
                <VStack align="start" spacing={4}>
                  <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_3_title || "CLOUD INFRASTRUCTURE"}
                  </Heading>
                  <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                    {settings?.projects_category_3_description || 
                     "Scalable cloud architectures on AWS, with automated deployment and monitoring solutions."
                    }
                  </Text>
                  <Text color="green.500" fontSize={{ base: "xs", md: "sm" }} textTransform="uppercase" letterSpacing="wide">
                    {settings?.projects_category_3_count || "25+ DEPLOYMENTS"}
                  </Text>
                </VStack>
              </VStack>
            </Box>
          </Grid>
        </VStack>

        {/* Recent Projects Grid */}
        <VStack spacing={{ base: 8, md: 12 }} w="100%">
          <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide" textAlign="center">
            RECENT PROJECTS
          </Heading>
          
          {projectsData?.results && projectsData.results.length > 0 ? (
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={{ base: 6, md: 8 }}>
              {projectsData.results.slice(0, 4).map((project) => (
                <Box key={project.id} bg="gray.900" p={{ base: 6, md: 8 }} border="1px solid" borderColor="gray.700">
                  <VStack align="start" spacing={6}>
                    <VStack align="start" spacing={3}>
                      <HStack spacing={2}>
                        <Box w={1.5} h={1.5} bg="green.500" borderRadius="full" />
                        <Text color="green.500" fontSize={{ base: "xs", md: "xs" }} textTransform="uppercase" letterSpacing="wide">
                          {project.category_display}
                        </Text>
                      </HStack>
                      <Heading size={{ base: "md", md: "lg" }} textTransform="uppercase" letterSpacing="wide">
                        {project.title}
                      </Heading>
                      <Text color="gray.300" fontSize={{ base: "xs", md: "sm" }} lineHeight="1.6">
                        {project.short_description}
                      </Text>
                    </VStack>
                    <HStack justify="space-between" w="100%" flexWrap={{ base: "wrap", md: "nowrap" }} spacing={{ base: 2, md: 4 }}>
                      <Link to={`/projects/${project.slug}`}>
                        <Button
                          variant="ghost"
                          color="white"
                          size="sm"
                          _hover={{ color: "green.500" }}
                          fontSize={{ base: "xs", md: "sm" }}
                        >
                          View Details →
                        </Button>
                      </Link>
                      <Text color="gray.400" fontSize={{ base: "xs", md: "xs" }}>
                        {project.tech_stack_display || project.technologies_list?.slice(0, 2).join(' + ')}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              ))}
            </Grid>
          ) : (
            <Box textAlign="center" py={12}>
              <Text color="gray.400" fontSize={{ base: "md", md: "lg" }}>
                No projects available yet. Check back soon!
              </Text>
            </Box>
          )}
        </VStack>

        {/* Call to Action */}
        <VStack spacing={8} textAlign="center" w="100%">
          <Heading size={{ base: "lg", md: "xl", lg: "3xl" }} textTransform="uppercase" letterSpacing="wide">
            {settings?.projects_cta_title || "START YOUR PROJECT TODAY"}
          </Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.300" maxW="3xl" lineHeight="1.6">
            {settings?.projects_cta_description || 
             "Ready to bring your ideas to life? Let's discuss how we can help you build a scalable, reliable solution that drives your business forward."
            }
          </Text>
          <Button
            bg="green.500"
            color="white"
            size={{ base: "md", md: "lg" }}
            _hover={{ bg: "green.600" }}
            textTransform="uppercase"
            letterSpacing="wide"
            px={{ base: 8, md: 12 }}
            onClick={() => window.location.href = '/contact'}
          >
            Get Started
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
};

export default Projects;